import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";	

import Swal from "sweetalert2";

import { FaRegUserCircle } from "react-icons/fa";

import Tag from '../../Atoms/Tag';
import Input from '../../Atoms/Input';
import TextArea from '../../Atoms/TextArea';
import Subtitle from '../../Atoms/Subtitle';
import Loader from "../../Atoms/Loader";

import Accordion from '../../Molecules/Accordion';
import Dropdown from '../../Molecules/Dropdown';
import DatePicker from '../../Molecules/DatePicker';
import CompanyComboBox from '../../Molecules/CompanyComboBox';
import InputTags from '../../Molecules/InputTags';
import MembersAvatars from '../../Molecules/MembersAvatars';

import InviteMembersModal from '../../Parts/InviteMembersModal';

import projectService from '../../../services/Projects/ProjectService';

import { projectTypesPT } from '../../../utils/projectTypes';
import { getTimeAgo } from "../../../utils/sharedFunctions";

export default function EditProjectTemplate({ project, companies, loading, keycloak }) {
    const navigate = useNavigate();
    
    const [inviteMembersModal, setInviteMembersModal] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [companyId, setCompanyId] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [companySector, setCompanySector] = useState('');
    const [focalPoint, setFocalPoint] = useState('');
    const [focalPointPosition, setFocalPointPosition] = useState('');
    const [focalPointEmail, setFocalPointEmail] = useState('');
    const [businessDescriptive, setBusinessDescriptive] = useState('');
    
    const [projectId, setProjectId] = useState('');
    const [projectName, setProjectName] = useState('');
    const [projectType, setProjectType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [goal, setGoal] = useState('');
    const [businessContext, setBusinessContext] = useState('');
    const [targetPublic, setTargetPublic] = useState('');

    const [expectedResults, setExpectedResults] = useState('');
    const [impactedBusinessIndicators, setImpactedBusinessIndicators] = useState([]);
    const [deliverables, setDeliverables] = useState([]);
    
    const [projectSummary, setProjectSummary] = useState('');
    const [promptSuggestions, setPromptSuggestions] = useState(['', '', '']);

        useEffect(() => {
        if (project !== null && project !== undefined) {
            setCompanyId(project.company.id || '');
            setCompanyName(project.company.name || '');
            setCompanySector(project.company.sector || '');
            setFocalPoint(project.focal_point_name || '');
            setFocalPointPosition(project.focal_point_position || '');
            setFocalPointEmail(project.focal_point_email || '');
            setBusinessDescriptive(project.business_description || '');

            setProjectId(project.id || '');
            setProjectName(project.name || '');
            setProjectType(project.project_type || '');
            setStartDate(project.start_date || '');
            setEndDate(project.end_date || '');
            setGoal(project.goal || '');
            setBusinessContext(project.business_context || '');
            setTargetPublic(project.target_public || '');

            setExpectedResults(project.expected_result || '');
            setImpactedBusinessIndicators(project.indicators?.map(i => i.name) || []);
            setDeliverables(project.deliverables?.map(d => d.name) || []);
            
            setProjectSummary(project.project_summary || '');
            setPromptSuggestions(
                Array.isArray(project.prompt_suggestions_business) && project.prompt_suggestions_business.length === 3
                    ? project.prompt_suggestions_business
                    : ['', '', '']
            );
        }
    }, [project]);
    
    const handleSubmit = async () => {
        try {
            setSubmitLoading(true);

            if (endDate && startDate >= endDate) {
                Swal.fire({
                    position: "top-end",
                    title: "Data de início não pode mais recente que a data de fim",
                    showConfirmButton: false,
                    timer: 2300,
                    background: '#27272A',
                    color: '#fff',
                });
                return;
            }

            const payload = {
                projectDataStore: {
                    customerData: {
                        company: {
                            id: companyId,
                            name: companyName,
                            sector: companySector
                        },
                        focalPoint,
                        focalPointPosition,
                        focalPointEmail,
                        businessDescritive: businessDescriptive
                    },
                    projectData: {
                        id: projectId,
                        title: projectName,
                        type: projectType,
                        startDate: startDate || null,
                        endDate: endDate || null,
                        goal,
                        businessContext,
                        targetPublic
                    },
                    impact: {
                        expectedResults,
                        impactedBusinessIndicators,
                        deliverables
                    }
                },
                projectSummary,
                promptSuggestions
            };

            await projectService.updateProject(projectId, payload, keycloak);

            Swal.fire({
                position: "top-end",
                title: "Projeto atualizado com sucesso!",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });

            navigate('/project/' + projectId);
        } catch (error) {
            Swal.fire({
                position: "top-end",
                title: "Erro ao processar projeto",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });
        } finally {
            setSubmitLoading(false);
        }
    }

    const handleCloseInviteMembersModal = () => {
        setInviteMembersModal(false);
    }

    const handleCancel = () => {
        navigate('/project/' + projectId);
    }

    const isFormDisabled = () => {
        const isCompanyValid = companyId && companyName && companySector;

        if (project !== 'null') {
            const mandatoryFields = {
                company: isCompanyValid,
                business_description: businessDescriptive,
                name: projectName,
                type: projectType,
                start_date: startDate,
                goal: goal,
                target_public: targetPublic,
                project_summary: projectSummary
            };

            return Object.values(mandatoryFields).some(field => 
                field === null || field === '' || field.trim() === '' || field === false
            );
        } else {
            return false;
        }
    }

    const buttonStyles = {
        enabled: {
            backgroundColor: '#8E51FF',
            fontWeight: 'bold',
            padding: '10px 20px',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
        },
        disabled: {
            backgroundColor: '#6B7280',
            fontWeight: 'bold',
            padding: '10px 20px',
            color: '#9CA3AF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'not-allowed'
        }
    };

    return (
        <>
            <InviteMembersModal
                isOpen={inviteMembersModal}
                onClose={handleCloseInviteMembersModal}
                projectId={projectId}
            />

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                    <Loader />
                </div>
            ): (
                <div className="container-xxl my-5">
                    <div id="edit-project-header" className="d-flex flex-row justify-content-between">
                        <h2>Editar {projectName || 'Carregando...'}</h2>

                        <button
                            onClick={() => setInviteMembersModal(true)}
                            className='btn d-flex flex-row align-items-center gap-2'
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#1F2A3A',
                            }}
                        >
                            <FaRegUserCircle />
                            Editar membros
                        </button>
                    </div>

                    <div>
                        <div className="d-flex flex-row align-items-center gap-2 mb-3">
                            <Tag text={companyName || 'Carregando...'} />

                            {project?.members?.length > 0 && (
                                <MembersAvatars members={project.members || []} />
                            )}

                            {project?.last_user_updated?.date && (
                                <p>{project?.last_user_updated?.date || 'xx/xx/xxxx'}</p>
                            )}

                            {project?.last_user_updated && (
                                <p>Editado por {project?.last_user_updated}</p>
                            )}

                            {project?.updatedAt && (
                                <p className="text-secondary">{getTimeAgo(project?.updatedAt)}</p>
                            )}
                        </div>

                        <div id="edit-project-description" className="mb-5">
                            <Subtitle>Defina as informações do seu projeto para obter soluções personalizadas</Subtitle>
                        </div>

                        <div id="edit-project-form">         
                            <Accordion title="Dados do cliente">
                                <div className='row' >
                                    <div className='col-4'>
                                        <CompanyComboBox
                                            label="Empresa"
                                            placeholder="Selecione a empresa"
                                            mandatory={true}
                                            items={companies}
                                            onSelect={(company) => {
                                                setCompanyId(company.id);
                                                setCompanyName(company.name);
                                                setCompanySector(company.sector);
                                            }}
                                            selectedCompanyId={companyId}
                                        />
                                    </div>

                                    <div className='col-8'>
                                        <Input
                                            label="Nome do ponto focal"
                                            placeholder="Digite o nome e sobrenome"
                                            mandatory={false}
                                            value={focalPoint}
                                            onChange={(e) => setFocalPoint(e.target.value)}
                                        />
                                    </div>

                                    <div className='col-8'>
                                        <Input
                                            label="Cargo e área do ponto focal"
                                            placeholder="Digite o cargo e a área (Ex: Gerente de TI)"
                                            mandatory={false}
                                            value={focalPointPosition}
                                            onChange={(e) => setFocalPointPosition(e.target.value)}
                                        />
                                    </div>

                                    <div className='col-4'>
                                        <Input
                                            label="E-mail do ponto focal"
                                            placeholder="Digite o e-mail"
                                            mandatory={false}
                                            value={focalPointEmail}
                                            onChange={(e) => setFocalPointEmail(e.target.value)}
                                        />
                                    </div>

                                    <div className='col-12'>
                                        <TextArea 
                                            label="Descritivo do negócio"
                                            placeholder="Descreva o negócio do cliente e principais produtos"
                                            mandatory={true}
                                            tooltipText="Exemplo: Grande empresa de energia com atuação nacional que possui 2 principais produtos, Geração Distribuida Compartilhada (GDC) e Mercado Livre"
                                            style={{ minHeight: '300px' }}
                                            value={businessDescriptive}
                                            onChange={(e) => setBusinessDescriptive(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </Accordion>

                            <Accordion title="Detalhes do projeto">
                                <div className='row'>
                                    <div className='col-12'>
                                        <Input
                                            label="Título do projeto"
                                            placeholder="Digite o título do projeto"
                                            mandatory={true}
                                            value={projectName}
                                            onChange={(e) => setProjectName(e.target.value)}
                                        />
                                    </div>

                                <div className='col-8'>
                                    <Dropdown
                                        label="Tipo de projeto"
                                        items={projectTypesPT}
                                        onSelect={setProjectType}
                                        mandatory={true}
                                        value={projectType}
                                    />
                                </div>

                                    <div className='col-2'>
                                         <DatePicker 
                                             label='Início do projeto' 
                                             mandatory={true} 
                                             value={startDate}
                                             onChange={setStartDate}
                                         />
                                     </div>

                                     <div className='col-2'>
                                         <DatePicker 
                                             label='Fim do projeto' 
                                             mandatory={false} 
                                             value={endDate}
                                             onChange={setEndDate}
                                         />
                                     </div>

                                    <div className='col-12'>
                                        <TextArea 
                                            label="Objetivo"
                                            placeholder="Descreva o objetivo do projeto."
                                            mandatory={true}
                                            tooltipText="Por exemplo: Conduzir pesquisa de mercado para identificar oportunidades e estratégias que aumentem a conversão e retenção de clientes PJ e PF no mercado de energia. "
                                            style={{ minHeight: '300px' }}
                                            value={goal}
                                            onChange={(e) => setGoal(e.target.value)}
                                        />
                                    </div>

                                    <div className='col-12'>
                                        <TextArea 
                                            label="Contexto do negócio"
                                            placeholder="Descreva o contexto do negócio, qual o cenário da empresa que originou o desafio do projeto"
                                            mandatory={false}
                                            tooltipText="Por exemplo: Em um cenário de transição e aumento da concorrência no setor de energia, a empresa perdeu market share em seus principais produtos. A ausência de uma cultura centrada no cliente agrava o desafio frente a novos players de perfil varejista. Portanto, o desafio é aprofundar o conhecimento sobre os clientes para desenvolver estratégias de canais e produtos mais eficazes, viabilizando a expansão da empresa neste novo mercado."
                                            style={{ minHeight: '300px' }}
                                            value={businessContext}
                                            onChange={(e) => setBusinessContext(e.target.value)}
                                        />
                                    </div>

                                    <div className='col-12'>
                                        <TextArea 
                                            label="Público alvo"
                                            placeholder="Descreva o público alvo do projeto"
                                            mandatory={true}
                                            tooltipText="Exemplo: Empresas que já migraram para o Mercado Livre de energia, Consumidores de conteúdo de reality shows, Time de TI da empresa, etc."
                                            style={{ minHeight: '300px' }}
                                            value={targetPublic}
                                            onChange={(e) => setTargetPublic(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </Accordion>

                            <Accordion title="Impacto">
                                <div className='row'>
                                    <div className='col-12'>
                                        <TextArea 
                                            label="Resultados esperados"
                                            placeholder="Descreva o que a empresa espera como resultado do projeto"
                                            mandatory={false}
                                            tooltipText="Exemplo: Expandir presença e reconhecimento de marca, Reduzir o Custo de Aquisição de Clientes (CAC), Redução do churn atual."
                                            style={{ minHeight: '300px' }}
                                            value={expectedResults}
                                            onChange={(e) => setExpectedResults(e.target.value)}
                                        />
                                    </div>

                                    <div className='col-12'>
                                        <InputTags
                                            label="Indicadores de negócio impactados"
                                            placeholder="Digite e pressione 'Enter' para adicionar novas tags."
                                            tooltipText="Exemplo: Custo de Aquisição (CAC), Churn Rate, NPS, Market Share, etc."
                                            value={impactedBusinessIndicators}
                                            onChange={setImpactedBusinessIndicators}
                                        />
                                    </div>

                                    <div className='col-12'>
                                        <InputTags
                                            label="Entregáveis"
                                            placeholder="Digite e pressione 'Enter' para adicionar novas tags"
                                            mandatory={false}
                                            tooltipText="Exemplo: Jornada AS IS, Perfis, Benchmark, Direcionamentos estratégicos, Cardápio de ideias, etc."
                                            value={deliverables}
                                            onChange={setDeliverables}
                                        />
                                    </div>
                                </div>
                            </Accordion>

                            <Accordion title="Atalhos na home do cliente">
                                <div className='row'>
                                    <div className='col-12'>
                                        <Subtitle>Essas perguntas aparecem como atalhos na Home do cliente e disparam respostas do assistente.</Subtitle>
                                    </div>

                                    {promptSuggestions.map((suggestion, index) => (
                                        <div className='col-12' key={index}>
                                            <Input
                                                label={`Pergunta sugerida ${index + 1}`}
                                                placeholder="Ex: Quais são os principais desafios do negócio?"
                                                mandatory={false}
                                                value={suggestion}
                                                onChange={(e) => {
                                                    const updated = [...promptSuggestions];
                                                    updated[index] = e.target.value;
                                                    setPromptSuggestions(updated);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Accordion>

                            <div className='w-auto d-flex flex-row justify-content-end gap-3'>
                                <button
                                    className='btn'
                                    onClick={handleCancel}
                                    style={{ 
                                        backgroundColor: 'transparent', 
                                        border: '1px solid #314158',
                                        color: '#fff',
                                        padding: '10px 20px',
                                        borderRadius: '6px'
                                    }}
                                >
                                    Voltar
                                </button>

                                {submitLoading ? (
                                    <button className='btn'>
                                        <Loader />
                                    </button>
                                ): (
                                    <button
                                        className='btn'
                                        onClick={handleSubmit}
                                        style={isFormDisabled() ? buttonStyles.disabled : buttonStyles.enabled}
                                        disabled={isFormDisabled()}
                                    >
                                        Salvar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
            </div>
            )}
        </>
    )
}